import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Radio, Car, Smartphone, Speaker, PhoneCall, Wrench, Sparkles } from "lucide-react";
import { PLANS } from "@/data/content";

const HIGHLIGHTS = [
  { icon: Car, label: "Car radio" },
  { icon: Smartphone, label: "Phone & app" },
  { icon: Speaker, label: "Streaming devices" },
];

export default function RadioSection() {
  const navigate = useNavigate();
  const plan = PLANS.find((p) => p.id === "radio") || PLANS[0];

  const go = () =>
    navigate(`/checkout?plan=${encodeURIComponent(plan.name)}&amount=${plan.price}`);

  return (
    <section id="radio" className="bg-white border-b border-[#E2E8F0]" data-testid="radio-section">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        {/* Visual */}
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="relative order-2 lg:order-1"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-[#E2E8F0]">
            <img
              src="https://images.unsplash.com/photo-1592570714618-15e2d4719c6c?auto=format&fit=crop&w=1000&q=80"
              alt="Car radio and audio system setup"
              className="w-full h-[22rem] object-cover"
              data-testid="radio-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#26395F]/70 via-transparent to-transparent" />
          </div>
          {/* Floating price badge */}
          <div className="absolute -bottom-5 -right-3 sm:right-6 bg-[#26395F] text-white rounded-2xl px-5 py-4 shadow-xl" data-testid="radio-price-badge">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#60A5FA]">One-time</p>
            <p className="font-display text-2xl font-bold leading-none mt-1">${plan.price}</p>
            <p className="text-[10px] text-white/60 mt-1">radio consultancy</p>
          </div>
        </motion.div>

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="order-1 lg:order-2"
        >
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">
            <Radio className="h-4 w-4" /> Radio Consultancy
          </span>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight uppercase mt-4 leading-[0.98] text-[#0F172A]">
            Radio setup &amp; app consultancy, <span className="text-[#3B82F6]">done for you</span>
          </h2>
          <p className="text-[#475569] mt-5 leading-relaxed max-w-xl">
            Our namesake service. Get expert, independent guidance for your radio &amp; streaming radio apps — and
            we help set it up and get it running across your car, phone and streaming devices. One-time charge, no
            subscription. We are an independent provider and not affiliated with any brand.
          </p>

          <div className="mt-6 flex flex-wrap gap-3" data-testid="radio-highlights">
            {HIGHLIGHTS.map((h) => (
              <span key={h.label} className="inline-flex items-center gap-2 rounded-full bg-[#F5F7FC] border border-[#E2E8F0] px-4 py-2 text-sm font-medium text-[#0F172A]">
                <h.icon className="h-4 w-4 text-[#3B82F6]" /> {h.label}
              </span>
            ))}
          </div>

          <ul className="mt-7 space-y-3">
            {plan.perks.map((perk) => (
              <li key={perk} className="flex items-start gap-3 text-sm text-[#334155]">
                <Check className="h-4 w-4 text-[#3B82F6] shrink-0 mt-0.5" />
                <span>{perk}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={go}
            className="btn-sweep mt-8 bg-[#3B82F6] text-white font-bold px-8 py-3.5 rounded-full hover:bg-[#2563EB] transition-colors duration-300"
            data-testid="radio-cta-btn"
          >
            Book radio consultancy — ${plan.price}
          </button>
        </motion.div>
        </div>

        {/* How it works */}
        <div className="mt-20 border-t border-[#E2E8F0] pt-12" data-testid="radio-how-it-works">
          <p className="text-center text-xs font-bold uppercase tracking-[0.25em] text-[#3B82F6]">How it works</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { n: "01", icon: PhoneCall, title: "Consult", text: "Tell us your setup — we give independent, expert guidance." },
              { n: "02", icon: Wrench, title: "Setup", text: "We set up and activate everything across your devices." },
              { n: "03", icon: Sparkles, title: "Enjoy", text: "Start listening — with a 15-day free-fix guarantee." },
            ].map((s) => (
              <div key={s.title} className="relative rounded-2xl bg-[#F5F7FC] border border-[#E2E8F0] p-6">
                <span className="absolute top-5 right-6 font-display text-3xl font-bold text-[#E2E8F0]">{s.n}</span>
                <div className="h-11 w-11 rounded-xl bg-[#3B82F6]/10 grid place-items-center"><s.icon className="h-5 w-5 text-[#3B82F6]" /></div>
                <h3 className="font-display text-lg uppercase tracking-tight mt-4 text-[#0F172A]">{s.title}</h3>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
